import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { useAppDispatch } from '~/store/appDispatch';
import { TRelatedCompanies } from './types.js';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.js';
import { useRelatedCompaniesForm } from './hooks.ts/useRelatedCompaniesForm.js';
import { updateForm } from '~/store/identification-form/thunks/thunks.js';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.js';
import { FormProvider } from 'react-hook-form';
import { Button, Box, Typography, Divider } from '@mui/material';
import { SeparateUnits } from './components/SeparateUnits.js';
import { RelatedEntityInfo } from './components/RelatedEntityInfo';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox';
import { styles } from '../../styles';
import { EMPTY_SEPARATE_UNIT, optionToEntityMap, testDataRelatedCompanies } from './constants';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { TIdentificationForm } from '~/store/identification-form/types.js';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { trimValues } from '~/modules/identification/utils.ts';
import { cleanEmpty, onInvalidForm } from '~/helpers/utils.js';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.js';
import { commonStyles } from '~/theme/styles/commonStyles.js';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.js';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.js';

interface IRelatedCompaniesProps {
  onNextStep: () => void;
}

export const RelatedCompanies = ({ onNextStep }: IRelatedCompaniesProps) => {
  const dispatch = useAppDispatch();

  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);

  const { isTestEnvironmentEnabled } = useFeatureFlags();
  const form = useRelatedCompaniesForm();

  const { isDirty, isSubmitting } = form.formState;

  const handleFillTestData = () =>
    form.reset(testDataRelatedCompanies, { keepDefaultValues: true });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TRelatedCompanies) => {
      let parsedValues = values;
      if (values.relatedEntityInfo?.entity === optionToEntityMap.option0) {
        parsedValues = { ...values, relatedEntityInfo: cleanEmpty(values.relatedEntityInfo) };
      }

      if (isDirty) {
        const data = {
          relatedCompanies: trimValues(parsedValues),
          applicationId: appId as number,
        };
        try {
          await dispatch(updateForm({ data, reportId: IdentificationFormSteps.relatedCompanies }));
          const formData = {
            field: IdentificationFormStepNames.relatedCompanies as keyof TIdentificationForm,
            data: cloneDeep(parsedValues),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  const handleCheck = (isChecked: boolean) => {
    form.setValue('separateUnits', isChecked ? [] : [EMPTY_SEPARATE_UNIT]);
  };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {isTestEnvironmentEnabled && (
          <Box sx={styles.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3 }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            Надайте інформацію про відокремлені підрозділи або підтвердіть їх відсутність
          </Typography>
          <FormCheckbox
            name="isSeparateUnitsAbsent"
            label="Відокремлені підрозділи відсутні"
            onChange={handleCheck}
          />
          <SeparateUnits />
          <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3, mt: 3 }}>
            <Divider sx={styles.divider} />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Надайте відомості про підпорядкованість корпоративній структурі
            </Typography>
          </Box>
          <RelatedEntityInfo />

          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            sx={{ alignSelf: 'center' }}
          >
            Продовжити
          </Button>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
};
